import { AfterViewInit, Component, Input, OnDestroy, OnInit, HostListener, ViewChildren, QueryList, ElementRef, inject } from '@angular/core';
import { AssemblyOrderModel } from 'src/app/models/assembly-order.model';
import { AssemblyOrderService } from 'src/app/services/assembly-order.service';
import { format, startOfWeek, addDays, isValid, parseISO } from 'date-fns';
import esAR from 'date-fns/locale/es';
import * as signalR from '@microsoft/signalr';
import { ConfigurationService } from 'src/app/shared/services/configuration.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { Guid } from 'guid-typescript';
import { MatIcon } from '@angular/material/icon';
import { Location, NgClass } from '@angular/common';
import { ManufacturingOrderComponent } from '../manufacturing-order/manufacturing-order.component';
import { ManufacturingOrderDetailsComponent } from '../manufacturing-order-details/manufacturing-order-details.component';

/**
 * Week shown last in this session. The menu link to this screen carries no query params, so without it every
 * visit started again on the current week.
 */
let lastWeekStart: Date | undefined;

import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';

@Component({
    selector: 'app-manufacturing-calendar',
    templateUrl: './manufacturing-calendar.component.html',
    styleUrls: ['./manufacturing-calendar.component.scss'],
    imports: [MatIcon, NgClass, ManufacturingOrderComponent, ManufacturingOrderDetailsComponent]
})
export class ManufacturingCalendarComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly assemblySvc = inject(AssemblyOrderService);
  private readonly router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  private readonly configSvc = inject(ConfigurationService);
  private readonly location = inject(Location);

  currentScreenWidth?: number;
  totalColumns: number = 7;
  visibleColumns: number = 6;
  dayList: {
    date: Date;
    description: string;
    orders: AssemblyOrderModel[];
  }[] = [];
  private initialDistance: number | null = null;
  assemblyOrders: AssemblyOrderModel[] = [];
  currentStartDate: Date = startOfWeek(new Date(), { weekStartsOn: 1 }); // week starts on Monday
  monthTitle: string = '';
  orderToPointId?: Guid;
  @Input() maxHeight?: number;

  selectedAssemblyOrder?: AssemblyOrderModel;
  showOrderDetails: boolean = false;

  /** Orders of the shown week are on their way (skeleton cards instead of empty columns). */
  loading = false;
  private ordersRequest = 0;
  /** The open order was pushed as a history entry by this screen, so closing it can just go Back. */
  private orderPushed = false;

  private hubConnection?: signalR.HubConnection;
  private queryParamSubscription?: Subscription;

  @ViewChildren('orderComp', { read: ElementRef })
  orderComponents!: QueryList<ElementRef>;
  timer?: ReturnType<typeof setTimeout>;

  constructor() {
    // ?week=yyyy-MM-dd (any day of the week) keeps the week on reload, Back and shared links.
    const week = this.activatedRoute.snapshot.queryParamMap.get('week');
    const fromUrl = week ? parseISO(week) : undefined;
    if (fromUrl && isValid(fromUrl)) {
      this.currentStartDate = startOfWeek(fromUrl, { weekStartsOn: 1 });
    } else if (lastWeekStart) {
      this.currentStartDate = lastWeekStart;
    }
    this.generateCurrentWeekDays();
  }

  ngOnInit() {
    this._initComponentTasks();
    this._subscribeToReloadEvents();
  }

  ngAfterViewInit() {
    this.getInitialCalculations();
  }

  _initComponentTasks() {
    this.initSignalRService();
    if (this.dayList.length > 0) this.getOrders();
  }

  _subscribeToReloadEvents() {
    this.queryParamSubscription = this.activatedRoute.queryParams.subscribe(
      (params) => {
        let queryParams = { ...params };

        if (queryParams['assemblyOrderId']) {
          this.getOAById(queryParams['assemblyOrderId']);
        }

        // ?order=<id> is the open order: the browser's Back closes it, Forward/reload reopens it.
        const orderId = queryParams['order'];
        if (!orderId && this.showOrderDetails) {
          this.closeOrderDetail();
        } else if (orderId && this.selectedAssemblyOrder?.id?.toString() !== orderId) {
          const loaded = this.assemblyOrders.find((x) => x.id?.toString() === orderId);
          if (loaded) this.showOrder(loaded);
          else this.getOAById(orderId);
        }

        // Navigate to same url without the removed query parameter (this shouldn't refresh component)
        delete queryParams['assemblyOrderId'];
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: queryParams,
          replaceUrl: true, // same page: no extra history entry for Back/Volver to land on
        });
      }
    );
  }

  getOAById(id: string) {
    this.assemblySvc.getAssemblyOrderById(id).subscribe((data) => {
      this.currentStartDate = startOfWeek(new Date(data.scheduleDate), {
        weekStartsOn: 1,
      }); // week starts on Monday new Date(data.scheduleDate);
      this.dayList = []; // Clear the current dayList
      this.generateCurrentWeekDays();
      this.rememberWeek();
      this.getOrders();
      this.showOrder(data);
    });
  }

  getInitialCalculations() {
    this.calculateColumns();
    setTimeout(() => {
      this.equalizeOrderComponentHeights();
    });
  }

  equalizeOrderComponentHeights() {
    let maxHeight = 0;

    // Find the tallest app-order-component
    this.orderComponents.forEach((compRef) => {
      if (compRef?.nativeElement) {
        const height = compRef.nativeElement.offsetHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      }
    });

    if (maxHeight > 0) this.maxHeight = maxHeight;
  }

  initSignalRService() {
    if (!this.hubConnection) {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .configureLogging(signalR.LogLevel.Information)
        .withUrl(this.configSvc.manufacturingApiUrl + 'hubs/orderupdates')
        .withAutomaticReconnect([0, 2000, 5000, 10000]) // Reconnect immediately, then after 2s, 5s, and 10s.
        .build();

      this.hubConnection
        .start()
        .then(() => {
          console.log('Connection started');
          this.subscribeToOrderUpdatesChannel();
        })
        .catch((err) => {
          return console.log('Error while starting connection: ' + err);
        });

      this.hubConnection.onreconnected((connectionId) => {
        console.info(`Reconnected. Connection ID: ${connectionId}`);
      });

      this.hubConnection.onreconnecting((error) => {
        console.warn(`Reconnecting due to ${error}`);
      });

      this.hubConnection.onclose((error) => {
        if (error) {
          console.warn(`Connection closed due to ${error}`);
          // Retry logic can also be placed here if required
        } else {
          console.info('Connection closed.');
        }
      });
    }
  }

  subscribeToOrderUpdatesChannel() {
    this.hubConnection?.on(
      'OrderUpdatesChannel',
      (data: AssemblyOrderModel) => {
        this.proccessSignalRData(data);
      }
    );
  }

  proccessSignalRData(data: AssemblyOrderModel) {
    this.getOrders(); //we need to get orders again in order to update order status (managed in BE when something changes)
  }

  private getOrders() {
    this.maxHeight = undefined; //reset maxHeigth to recalculate it

    let startDate = this.dayList[0].date;
    startDate.setHours(0, 0, 0, 0);

    let endDate = this.dayList[this.dayList.length - 1].date;
    endDate.setHours(23, 59, 59, 999);

    this.getManufacturingOrderByWeek(
      startDate.toISOString(),
      endDate.toISOString()
    );
  }

  /** Click on a card: open it and add a history entry (?order=<id>) so Back returns to the week. */
  openOrderDetail(order: AssemblyOrderModel) {
    this.showOrder(order);
    this.orderPushed = true;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { order: order.id?.toString() },
      queryParamsHandling: 'merge',
    });
  }

  private showOrder(order: AssemblyOrderModel) {
    if (this.timer != undefined) {
      clearTimeout(this.timer);
      this.orderToPointId = undefined;
    }
    this.selectedAssemblyOrder = order;
    this.showOrderDetails = true;
  }

  generateCurrentWeekDays() {
    this.dayList = []; // Clear the existing dayList to prevent accumulation of days

    for (let i = 0; i < 7; i++) {
      const currentDate = addDays(this.currentStartDate, i);
      const formattedDate = format(currentDate, 'EEEE dd', { locale: esAR });
      this.dayList.push({
        date: currentDate,
        description: formattedDate.toLocaleUpperCase(),
        orders: [],
      });
    }

    //Get Month title
    const firstDayOfWeek = this.dayList[0].date;
    const rawMonthTitle = format(firstDayOfWeek, 'MMMM yyyy', { locale: esAR });
    this.monthTitle =
      rawMonthTitle.charAt(0).toUpperCase() + rawMonthTitle.slice(1);
  }

  navigateWeek(direction: number) {
    this.currentStartDate = addDays(this.currentStartDate, direction * 7);
    this.dayList = []; // Clear the current dayList
    this.generateCurrentWeekDays();
    this.rememberWeek();
    this.getOrders();
  }

  /** Week in the URL (replaceUrl: changing weeks does not pile up history entries) and in the session. */
  private rememberWeek() {
    lastWeekStart = this.currentStartDate;
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { week: format(this.currentStartDate, 'yyyy-MM-dd') },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  getManufacturingOrderByWeek(from: string, to: string) {
    const request = ++this.ordersRequest;
    this.loading = true;
    this.assemblySvc.getAssemblyOrders(from, to).subscribe({
      error: (err) => {
        console.error(err);
        if (request === this.ordersRequest) this.loading = false;
      },
      next: (orders: AssemblyOrderModel[]) => {
        // Clicking through weeks quickly: ignore the answer of a week that is no longer on screen.
        if (request !== this.ordersRequest) return;
        this.loading = false;
        this.assemblyOrders = [...[], ...orders];
        this.distributeOrders();

        let selectedId: Guid | undefined;

        if (this.selectedAssemblyOrder && this.showOrderDetails) {
          selectedId = this.selectedAssemblyOrder!.id;
          this.selectedAssemblyOrder = undefined;
          this.showOrderDetails = false;
        }

        setTimeout(() => {
          this.equalizeOrderComponentHeights();

          if (selectedId) {
            let selected = this.assemblyOrders.find((x) => x.id == selectedId);
            if (selected) {
              this.selectedAssemblyOrder = { ...{}, ...selected };
              this.showOrderDetails = true;
            }
          }
        });
      },
    });
  }

  distributeOrders() {
    for (let day of this.dayList) {
      day.orders = this.assemblyOrders.filter((order) => {
        const orderDate = new Date(order.scheduleDate);
        return (
          orderDate.getFullYear() === day.date.getFullYear() &&
          orderDate.getMonth() === day.date.getMonth() &&
          orderDate.getDate() === day.date.getDate()
        );
      });
    }
  }

  zoomOut() {
    if (this.visibleColumns < 7) {
      this.visibleColumns++;
    }
  }

  zoomIn() {
    if (this.visibleColumns > 1) {
      this.visibleColumns--;
    }
  }

  getDynamicWidth(index: number): string {
    return (100 / this.visibleColumns).toFixed(2) + '%';
  }

  notifyChanges(order: AssemblyOrderModel) {
    const index = this.assemblyOrders.findIndex((x) => x.id == order.id);
    if (index > -1) {
      this.assemblyOrders[index] = order;
      this.distributeOrders();
    }
    this.refreshOrderStatus(order.id);
  }

  /**
   * El estado de la OA (y con él el color del borde) lo recalcula el backend al cambiar o editar un ítem; la copia
   * local del detalle trae el estado anterior. Se pide la OA de nuevo para no depender solo del aviso de SignalR, y
   * se actualiza en el lugar para que el detalle abierto no se cierre ni pierda lo que estaba expandido.
   */
  private refreshOrderStatus(id: Guid) {
    this.assemblySvc.getAssemblyOrderById(id.toString()).subscribe({
      next: (updated) => {
        const targets = [this.assemblyOrders.find((x) => x.id == id), this.selectedAssemblyOrder];
        for (const target of targets) {
          if (target && target.id == id) {
            target.statusId = updated.statusId;
            target.statusObservation = updated.statusObservation;
          }
        }
      },
      // Si falla, queda el aviso de SignalR, que vuelve a pedir la semana.
      error: () => undefined,
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === '+') {
      this.zoomIn();
    } else if (event.key === '-') {
      this.zoomOut();
    }
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 2) {
      const deltaX = event.touches[0].clientX - event.touches[1].clientX;
      const deltaY = event.touches[0].clientY - event.touches[1].clientY;
      this.initialDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (event.touches.length === 2 && this.initialDistance !== null) {
      const deltaX = event.touches[0].clientX - event.touches[1].clientX;
      const deltaY = event.touches[0].clientY - event.touches[1].clientY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      if (distance > this.initialDistance) {
        this.zoomIn();
      } else if (distance < this.initialDistance) {
        this.zoomOut();
      }

      this.initialDistance = distance;
    }
  }

  onTouchEnd(): void {
    this.initialDistance = null;
  }

  onScroll(event: WheelEvent) {
    if (event.deltaY > 0) {
      this.zoomOut();
    } else {
      this.zoomIn();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.getInitialCalculations();
  }

  calculateColumns() {
    const screenWidth = (this.currentScreenWidth = window.innerWidth);

    switch (true) {
      case screenWidth < 650:
        this.visibleColumns = 1;
        break;
      case screenWidth >= 650 && screenWidth < 768:
        this.visibleColumns = 2;
        break;
      case screenWidth >= 768 && screenWidth < 1000:
        this.visibleColumns = 3;
        break;
      case screenWidth >= 1000 && screenWidth < 1200:
        this.visibleColumns = 4;
        break;
      case screenWidth >= 1200 && screenWidth < 1350:
        this.visibleColumns = 5;
        break;
      case screenWidth > 1350:
      default:
        this.visibleColumns = 6;
    }
  }

  trackById(index: number, item: AssemblyOrderModel) {
    return item.id;
  }

  ngOnDestroy() {
    this.hubConnection?.stop();
    this.queryParamSubscription?.unsubscribe();
  }

  preventInteraction(order: AssemblyOrderModel) {
    return order.statusId !== 6 && order.statusId !== 7 && order.statusId !== 9;
  }

  /** "Volver al calendario", Escape: same as the browser's Back when the order was opened from this screen. */
  @HostListener('window:keydown.escape', ['$event'])
  backToCalendar(event?: KeyboardEvent) {
    if (!this.showOrderDetails) return;
    if (this.orderPushed) {
      this.orderPushed = false;
      this.location.back();
      return;
    }
    this.closeOrderDetail();
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { order: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private closeOrderDetail() {
    this.orderPushed = false;
    this.orderToPointId = this.selectedAssemblyOrder?.id;
    this.showOrderDetails = false;
    this.selectedAssemblyOrder = undefined;

    this.timer = setTimeout(() => {
      this.orderToPointId = undefined;
    }, 3000);
  }
}
